







































































































































































import { mapGetters } from 'vuex';

import {
  EAiStreamerIntegrationModeStatus,
  TCopyStakeOperatorAiStreamerConfigurationResponse,
  EAiStreamerConnectionModeStatus,
  TCopyStakeOperatorAiStreamerConfigurationPayload,
  TCopyStakeSettingsElevenLabsConfigurationResponse,
  TCopyStakeSettingsElevenLabsConfigurationPayload
} from '@/api/schema';

import { errorToastMessage } from '@/helpers/errorToastMessage';
import { cloneDeep, isEqual } from 'lodash';
import {
  getGlobalAiStreamerConfig,
  getOperatorAiStreamerConfig,
  updateOperatorAiStreamerConfig
} from '@/api/CopyStake/AutoClicker';
import {
  getAiStreamerIntegrationElevenLabsConfiguration,
  updateAiStreamerIntegrationElevenLabsConfiguration
} from '@/api/CopyStake/Settings';

type TConfigData = {
  heygenEnabled: typeof EAiStreamerIntegrationModeStatus;
  elevenlabsEnabled: typeof EAiStreamerIntegrationModeStatus;
  connectionMode: EAiStreamerConnectionModeStatus;
  imageVersion: string;
};
const defaultConfigData = (): TConfigData => ({
  heygenEnabled: EAiStreamerIntegrationModeStatus.USE_GLOBAL,
  elevenlabsEnabled: EAiStreamerIntegrationModeStatus.USE_GLOBAL,
  connectionMode: EAiStreamerConnectionModeStatus.INCLUSTER,
  imageVersion: ''
});

type TElevenLabsData = {
  apiKey: string;
};
const defaultElevenLabsData = (): TElevenLabsData => ({
  apiKey: ''
});

export default {
  name: 'CopyStakeSettingsAiStreamerIntegration',
  data(): any {
    return {
      EAiStreamerIntegrationModeStatus,
      EAiStreamerConnectionModeStatus,

      configSavedData: defaultConfigData(),
      configData: defaultConfigData(),

      elevenLabsSavedData: defaultElevenLabsData(),
      elevenLabsData: defaultElevenLabsData(),

      globalSettings: {
        dockerImage: '',
        heygenEnabled: false,
        elevenlabsEnabled: false
      },

      showKeys: {
        elevenlabs: false
      },

      isLoading: false,
      formValid: false
    };
  },
  props: {
    title: {
      type: String,
      default: (): string => ''
    }
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),

    canEdit(): boolean {
      return this.$role.can.update('copy_stake streamers');
    },

    configDataChanged(): boolean {
      return !isEqual(this.configData, this.configSavedData);
    },
    elevenLabsDataChanged(): boolean {
      return !isEqual(this.elevenLabsData, this.elevenLabsSavedData);
    },
    valueCanBeSaved(): boolean {
      return (
        this.formValid &&
        (this.configDataChanged || this.elevenLabsDataChanged)
      );
    }
  },
  watch: {
    operatorId: {
      handler: 'getConfiguration',
      immediate: true
    }
  },
  methods: {
    parseConfigData(
      data: TCopyStakeOperatorAiStreamerConfigurationResponse
    ): void {
      const parsedData: TConfigData = {
        heygenEnabled:
          data.heygenEnabled ?? EAiStreamerIntegrationModeStatus.USE_GLOBAL,
        elevenlabsEnabled:
          data.elevenlabsEnabled ??
          EAiStreamerIntegrationModeStatus.USE_GLOBAL,
        connectionMode:
          data.connectionMode ?? EAiStreamerConnectionModeStatus.INCLUSTER,
        imageVersion: data.imageVersion ?? ''
      };

      this.configSavedData = cloneDeep(parsedData);
      this.configData = cloneDeep(parsedData);
    },
    parseElevenLabsData(
      data: TCopyStakeSettingsElevenLabsConfigurationResponse
    ): void {
      const parsedData: TElevenLabsData = {
        apiKey: data.apiKey ?? ''
      };

      this.elevenLabsData = cloneDeep(parsedData);
      this.elevenLabsSavedData = cloneDeep(parsedData);
    },

    async getConfiguration(): Promise<void> {
      if (!this.operatorId) {
        return;
      }

      this.isLoading = true;

      try {
        const results = await Promise.allSettled([
          getGlobalAiStreamerConfig(),
          getOperatorAiStreamerConfig(),
          getAiStreamerIntegrationElevenLabsConfiguration()
        ]);

        const [globalResult, operatorResult, elevenLabsResult] = results;

        if (globalResult.status === 'fulfilled') {
          this.globalSettings = {
            dockerImage: globalResult.value.imageVersion,
            heygenEnabled: globalResult.value.heygenEnabled,
            elevenlabsEnabled: globalResult.value.elevenlabsEnabled
          };
        } else {
          errorToastMessage(globalResult.reason);
        }

        if (operatorResult.status === 'fulfilled') {
          this.parseConfigData(operatorResult.value);
        } else {
          errorToastMessage(operatorResult.reason);
        }

        if (elevenLabsResult.status === 'fulfilled') {
          this.parseElevenLabsData(elevenLabsResult.value);
        } else {
          errorToastMessage(elevenLabsResult.reason);
        }
      } catch (err) {
        errorToastMessage(err);
      } finally {
        this.isLoading = false;
      }
    },

    async updateConfiguration(): Promise<void> {
      const isFormValid = await (this.$refs.form as any).validate();
      if (!isFormValid) return;

      if (!this.configDataChanged && !this.elevenLabsDataChanged) {
        return;
      }

      this.isLoading = true;

      try {
        const requests: Promise<void>[] = [];

        if (this.configDataChanged) {
          const payload: TCopyStakeOperatorAiStreamerConfigurationPayload = {
            ...this.configData,
            imageVersion: this.configData.imageVersion || null
          };

          requests.push(
            updateOperatorAiStreamerConfig(payload).then(
              (data: TCopyStakeOperatorAiStreamerConfigurationResponse) => {
                this.parseConfigData(data);
              }
            )
          );
        }

        if (this.elevenLabsDataChanged) {
          const payload: TCopyStakeSettingsElevenLabsConfigurationPayload = {
            apiKey:
              this.elevenLabsData.apiKey?.length > 0
                ? this.elevenLabsData.apiKey
                : null
          };

          requests.push(
            updateAiStreamerIntegrationElevenLabsConfiguration(payload).then(
              (data: TCopyStakeSettingsElevenLabsConfigurationResponse) => {
                this.parseElevenLabsData(data);
              }
            )
          );
        }

        await Promise.all(requests);

        this.$toast.success(
          'AI Streamer Integration settings were successfully updated!'
        );
      } catch (error) {
        errorToastMessage(error);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
